import React, { createContext, useState, useEffect, useRef, ReactNode } from "react";
import { encryptPassword } from "../utils/passwordEncryption";
import { auth, googleProvider } from "../lib/firebase";
import { signInWithPopup, signInWithEmailAndPassword, onAuthStateChanged, signOut as firebaseSignOut } from "firebase/auth";

// Get the backend API base URL (set via VITE_API_URL during build)
const API_BASE_URL = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  role: "user" | "admin";
}

export interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  logout: () => void;
  loginWithGoogle: () => Promise<void>;
  exchangeFirebaseToken: () => Promise<void>;
  verifyToken: () => Promise<boolean>;
  updatePassword: (email: string, newPassword: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Ref so onAuthStateChanged closure always sees the latest token without re-subscribing
  const tokenRef = useRef<string | null>(null);

  useEffect(() => { tokenRef.current = token; }, [token]);

  // Load token from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem("authToken");
    if (storedToken) {
      setToken(storedToken);
      verifyToken(storedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  // Listen to Firebase auth state — auto-provisions Orchy account for any verified Firebase user
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) return;
      if (tokenRef.current && user && user.email?.toLowerCase() === firebaseUser.email?.toLowerCase()) return;

      // Reload to get the latest emailVerified flag from Firebase servers.
      // Without this, a page refresh after email verification still sees the
      // old cached emailVerified=false and skips provisioning.
      if (!firebaseUser.emailVerified) {
        try { await firebaseUser.reload(); } catch { /* ignore, use cached state */ }
      }
      if (!auth.currentUser?.emailVerified) return;

      try {
        // Force-refresh ensures we get a valid token, not a cached stale one
        const idToken = await auth.currentUser.getIdToken(true);
        await exchangeFirebaseIdToken(idToken);
      } catch (e: any) {
        console.warn("Firebase auth state exchange notice:", e?.message);
        // Only sign out for Firebase auth-level errors (revoked token, deleted account).
        // Never sign out for network errors or backend failures — the user should still
        // be able to sign in manually via the login form.
        const code: string = (e as any)?.code ?? "";
        if (code.startsWith("auth/")) {
          firebaseSignOut(auth).catch(() => {});
        }
      }
    });
    return () => unsubscribe();
  }, [user]);

  const verifyToken = async (tokenToVerify?: string) => {
    const authToken = tokenToVerify || token;
    if (!authToken) {
      setIsLoading(false);
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        // Expired or invalid token — gracefully clean up stale session state
        setUser(null);
        setToken(null);
        tokenRef.current = null;
        localStorage.removeItem("authToken");

        // If Firebase currently has a signed-in user, refresh their session
        if (auth.currentUser?.emailVerified) {
          try {
            const idToken = await auth.currentUser.getIdToken(true);
            await exchangeFirebaseIdToken(idToken);
            return true;
          } catch {
            // Ignore exchange failure
          }
        }
        return false;
      }

      // Check if response is JSON before parsing
      const contentType = response.headers.get("content-type");
      if (!contentType?.includes("application/json")) {
        setUser(null);
        setToken(null);
        tokenRef.current = null;
        localStorage.removeItem("authToken");
        return false;
      }

      const data = await response.json();
      if (data.success && data.data?.user) {
        setUser({ ...data.data.user, role: data.data.user.role ?? "user" });
        setToken(authToken);
        tokenRef.current = authToken;
        localStorage.setItem("authToken", authToken);
        return true;
      } else {
        setUser(null);
        setToken(null);
        tokenRef.current = null;
        localStorage.removeItem("authToken");
        return false;
      }
    } catch {
      setUser(null);
      setToken(null);
      tokenRef.current = null;
      localStorage.removeItem("authToken");
    } finally {
      setIsLoading(false);
    }

    return false;
  };

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      // Try Firebase email/password first (covers accounts created via the Firebase signup flow)
      try {
        const result = await signInWithEmailAndPassword(auth, email, password);
        if (!result.user.emailVerified) {
          await firebaseSignOut(auth);
          throw new Error("Please verify your email before signing in. Check your inbox for the verification link.");
        }
        const idToken = await result.user.getIdToken();
        await exchangeFirebaseIdToken(idToken); // creates MongoDB user on first login
        return;
      } catch (firebaseErr: any) {
        const code: string = firebaseErr?.code ?? "";
        // Only fall through to Orchy login on credential errors; everything else bubbles up
        const isCredentialError = code === "auth/user-not-found" || code === "auth/wrong-password" ||
                                  code === "auth/invalid-credential" || code === "auth/invalid-login-credentials";
        if (!isCredentialError) throw firebaseErr;
      }

      // Fall back to Orchy native login (for accounts created via /auth/signup)
      const encryptedPassword = await encryptPassword(password);
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password: encryptedPassword }),
      });

      const contentType = response.headers.get("content-type");
      const isJson = contentType?.includes("application/json");

      if (!response.ok) {
        if (isJson) {
          const error = await response.json();
          throw new Error(error.error || "Invalid email or password.");
        }
        throw new Error(`Login failed (${response.status}). Please check your credentials and try again.`);
      }

      const data = await response.json();
      if (data.success) {
        setUser({ ...data.data.user, role: data.data.user.role ?? "user" });
        setToken(data.data.token);
        localStorage.setItem("authToken", data.data.token);
      }
    } catch (error: any) {
      console.error("Login error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (email: string, password: string, firstName: string, lastName: string) => {
    setIsLoading(true);
    try {
      // Encrypt password before sending
      const encryptedPassword = await encryptPassword(password);
      
      const response = await fetch(`${API_BASE_URL}/auth/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password: encryptedPassword, firstName, lastName }),
      });

      if (!response.ok) {
        // Check if response is JSON before parsing
        const contentType = response.headers.get("content-type");
        if (!contentType?.includes("application/json")) {
          throw new Error(`Server error (${response.status}): ${response.statusText}. API endpoint may not be accessible.`);
        }

        try {
          const error = await response.json();
          throw new Error(error.error || "Signup failed");
        } catch (parseError: any) {
          throw new Error(`Signup failed with status ${response.status}`);
        }
      }

      const data = await response.json();
      if (data.success) {
        setUser({ ...data.data.user, role: data.data.user.role ?? "user" });
        setToken(data.data.token);
        localStorage.setItem("authToken", data.data.token);
      }
    } catch (error: any) {
      console.error("Signup error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("authToken");
    firebaseSignOut(auth).catch(() => {/* best-effort */});
  };

  /** Exchange a Firebase ID token for an Orchy session token. */
  const exchangeFirebaseIdToken = async (idToken: string): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/auth/firebase`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ idToken }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as { error?: string };
      throw new Error(err.error ?? "Google sign-in failed");
    }

    const data = await response.json() as { success: boolean; data: { token: string; user: AuthUser } };
    if (data.success) {
      setUser({ ...data.data.user, role: data.data.user.role ?? "user" });
      setToken(data.data.token);
      tokenRef.current = data.data.token;
      localStorage.setItem("authToken", data.data.token);
    }
  };

  const loginWithGoogle = async () => {
    setIsLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();
      await exchangeFirebaseIdToken(idToken);
    } catch (error: any) {
      const code: string = error?.code ?? "";

      // Popup closed/cancelled — Firebase may still have authenticated the user.
      // Re-try with auth.currentUser before deciding whether to surface an error.
      if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
        const currentUser = auth.currentUser;
        if (currentUser?.providerData.some(p => p.providerId === "google.com")) {
          // User did authenticate; the popup just closed early — provision account.
          const idToken = await currentUser.getIdToken();
          await exchangeFirebaseIdToken(idToken); // throws on backend failure — caller shows error
          return;
        }
        // No current user — genuine cancellation, stay silent.
        return;
      }

      // Popup was blocked — surface a clear message instead of nothing.
      if (code === "auth/popup-blocked") {
        throw new Error("Pop-up was blocked by your browser. Please allow pop-ups for this site and try again.");
      }

      console.error("Google login error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /** Exchange the current Firebase session (e.g. after email/password sign-up) for an Orchy JWT. */
  const exchangeFirebaseToken = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("No active Firebase session.");
    setIsLoading(true);
    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/auth/firebase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(err.error ?? "Authentication failed");
      }
      const data = await response.json() as { success: boolean; data: { token: string; user: AuthUser } };
      if (data.success) {
        setUser({ ...data.data.user, role: data.data.user.role ?? "user" });
        setToken(data.data.token);
        localStorage.setItem("authToken", data.data.token);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const updatePassword = async (email: string, newPassword: string) => {
    setIsLoading(true);
    try {
      // Encrypt password before sending
      const encryptedPassword = await encryptPassword(newPassword);
      
      const response = await fetch(`${API_BASE_URL}/auth/update-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, newPassword: encryptedPassword }),
      });

      if (!response.ok) {
        // Check if response is JSON before parsing
        const contentType = response.headers.get("content-type");
        if (!contentType?.includes("application/json")) {
          throw new Error(`Server error (${response.status}): ${response.statusText}. API endpoint may not be accessible.`);
        }

        try {
          const error = await response.json();
          throw new Error(error.error || "Failed to update password");
        } catch (parseError: any) {
          throw new Error(`Failed to update password with status ${response.status}`);
        }
      }

      const data = await response.json();
      if (data.success) {
        // Password updated successfully
        logout(); // Log out the user so they can login with new password
      }
    } catch (error: any) {
      console.error("Update password error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        isAuthenticated: !!user,
        isAdmin: user?.role === "admin",
        login,
        signup,
        logout,
        loginWithGoogle,
        exchangeFirebaseToken,
        verifyToken,
        updatePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
